/**
 * Reference Data Policy
 *
 * Reacts after setProcess to resolve reference data (publication names,
 * warehouse names, channel names) needed by the operation renderer.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { BatchProcessesDetailState, ResolvedPublication, ResolvedWarehouse, ResolvedChannel } from '../state';
import { BatchProcessesDetailCommand } from '../commands';
import { getRenderer } from '../../components/operationRenderers';

export const createReferenceDataPolicy = (
  backend: ClientLibraryType
): Policy<BatchProcessesDetailState, BatchProcessesDetailCommand> => ({
  name: 'reference-data',

  shouldReact: ( prev, next ) =>
    prev.process === null && next.process !== null,

  onStateChange: async ({ next, ctx }) => {
    const { process } = next;
    if ( !process ) return;

    const renderer = getRenderer( process.operationName );
    const { publicationIds, warehouseIds, channelIds } = renderer.getRequiredReferenceIds( process );

    // Merge renderer-requested publication IDs with completed + errored IDs
    const allPublicationIds = new Set<string>( publicationIds ?? []);

    if ( process.completedTargetIds ) {
      for ( const id of process.completedTargetIds ) {
        allPublicationIds.add( id );
      }
    }

    if ( process.erroredTargetIds ) {
      for ( const entry of process.erroredTargetIds ) {
        const pubId = ( entry as Record<string, unknown> ).targetId as string;
        if ( pubId ) allPublicationIds.add( pubId );
      }
    }

    const mergedPublicationIds = Array.from( allPublicationIds );

    const hasPublications = mergedPublicationIds.length > 0;
    const hasWarehouses = warehouseIds && warehouseIds.length > 0;
    const hasChannels = channelIds && channelIds.length > 0;

    // Nothing to resolve
    if ( !hasPublications && !hasWarehouses && !hasChannels ) return;

    ctx.dispatch(
      { type: 'batchProcesses/detail/loading/setReferenceDataLoading', payload: { isLoading: true }},
      { source: 'api' }
    );

    try {
      const promises: Promise<void>[] = [];

      // Resolve publications by fetching each one by ID
      if ( hasPublications ) {
        const pubPromise = Promise.all(
          mergedPublicationIds.map( id =>
            backend.queries.ItemsPublishingPublicationsById( id )
              .then( pub => {
                const raw = pub as Record<string, unknown>;
                const rawTitle = raw.title;
                const title = typeof rawTitle === 'string'
                  ? rawTitle
                  : (( rawTitle as Record<string, string> )?.es
                    || ( rawTitle as Record<string, string> )?.en
                    || ( rawTitle as Record<string, string> )?.pt
                    || id );
                const sku = raw.publicationSku as string | undefined;
                return { id: pub.id, title, thumbnail: raw.thumbnail as string | undefined, sku };
              })
              .catch(() => ({ id, title: id, thumbnail: undefined, sku: undefined })))
        ).then( results => {
          const publications: Record<string, ResolvedPublication> = {};
          for ( const pub of results ) {
            publications[pub.id] = pub;
          }
          ctx.dispatch(
            { type: 'batchProcesses/detail/data/setResolvedPublications', payload: { publications }},
            { source: 'api' }
          );
        });
        promises.push( pubPromise );
      }

      // Resolve warehouses — fetch all, filter client-side
      if ( hasWarehouses ) {
        const whPromise = backend.queries.StockManagementWarehouses()
          .then( allWarehouses => {
            const warehouseSet = new Set( warehouseIds );
            const warehouses: Record<string, ResolvedWarehouse> = {};
            for ( const wh of allWarehouses ) {
              if ( warehouseSet.has(( wh as any ).id )) {
                warehouses[( wh as any ).id] = { id: ( wh as any ).id, name: ( wh as any ).name ?? '' };
              }
            }
            ctx.dispatch(
              { type: 'batchProcesses/detail/data/setResolvedWarehouses', payload: { warehouses }},
              { source: 'api' }
            );
          });
        promises.push( whPromise );
      }

      // Resolve channels — fetch all, filter client-side
      if ( hasChannels ) {
        const chPromise = backend.queries.ExternalSystemsIntegrations()
          .then( allChannels => {
            const channelSet = new Set( channelIds );
            const channels: Record<string, ResolvedChannel> = {};
            for ( const ch of allChannels ) {
              if ( channelSet.has(( ch as any ).connectorId )) {
                channels[( ch as any ).connectorId] = {
                  connectorId: ( ch as any ).connectorId,
                  name: ( ch as any ).name ?? '',
                  icon: ( ch as any ).icon
                };
              }
            }
            ctx.dispatch(
              { type: 'batchProcesses/detail/data/setResolvedChannels', payload: { channels }},
              { source: 'api' }
            );
          });
        promises.push( chPromise );
      }

      await Promise.all( promises );
    } catch ( error ) {
      console.error( 'Error fetching reference data:', error );
    } finally {
      ctx.dispatch(
        { type: 'batchProcesses/detail/loading/setReferenceDataLoading', payload: { isLoading: false }},
        { source: 'api' }
      );
    }
  }
});
